/**
 * src/core/signal.js
 * 
 * Sistema de reactividad: Signals, Computed, Effects y Batch
 */

let signalIdCounter = 0
let effectIdCounter = 0

class Signal {
    constructor(value) {
        this.value = value
        this.subs = new Set()
        this.id = `signal_${signalIdCounter++}`
    }

    get() {
        if (Signal.current && Signal.current.deps) {
            this.subs.add(Signal.current)
            Signal.current.deps.add(this)
        }
        return this.value
    }

    set(value) {
        if (Object.is(value, this.value)) return
        this.value = value
        this.subs.forEach(sub => {
            try {
                if (sub && typeof sub.dirty === 'function') {
                    sub.dirty()
                }
            } catch (error) {
                console.error('Error in signal subscriber:', error)
            }
        })
    }
}

class Computed {
    constructor(fn) {
        this.fn = fn
        this.value = undefined
        this.isDirty = true
        this.subs = new Set()
        this.deps = new Set()
    }

    get() {
        if (this.isDirty) {
            this.deps.forEach(d => d.subs.delete(this))
            this.deps.clear()
            const prev = Signal.current
            Signal.current = this
            this.value = this.fn()
            Signal.current = prev
            this.isDirty = false
        }
        if (Signal.current && Signal.current !== this && Signal.current.deps) {
            this.subs.add(Signal.current)
            Signal.current.deps.add(this)
        }
        return this.value
    }

    dirty() {
        if (this.isDirty) return
        this.isDirty = true
        this.subs.forEach(sub => sub.dirty())
    }
}

class Effect {
    constructor(fn) {
        this.fn = fn
        this.cleanupFn = null
        this.deps = new Set()
        this.run()
    }

    run() {
        Effect.pending.delete(this)
        const prev = Signal.current
        Signal.current = this
        this.deps.forEach(d => d.subs.delete(this))
        this.deps.clear()

        try {
            if (this.cleanupFn) {
                this.cleanupFn()
                this.cleanupFn = null
            }

            const result = this.fn()
            if (typeof result === 'function') {
                this.cleanupFn = result
            }
        } catch (error) {
            console.error('Error in effect:', error)
        } finally {
            Signal.current = prev
        }
    }

    dirty() {
        Effect.pending.add(this)
        if (!Effect.scheduled) {
            Effect.scheduled = true
            Promise.resolve().then(Effect.flush)
        }
    }

    cleanup() {
        try {
            if (this.cleanupFn) {
                this.cleanupFn()
                this.cleanupFn = null
            }
        } catch (error) {
            console.error('Error in effect cleanup:', error)
        }
        Effect.pending.delete(this)
        this.deps.forEach(d => d.subs.delete(this))
    }
}

class Batch {
    static run(fn) {
        const wasScheduled = Effect.scheduled
        Effect.scheduled = true
        try {
            return fn()
        } finally {
            Effect.scheduled = wasScheduled
            if (!wasScheduled) Effect.flush()
        }
    }
}

Effect.pending = new Set()
Effect.scheduled = false
Effect.flush = () => {
    Effect.scheduled = false
    Effect.pending.forEach(e => e.run())
    Effect.pending.clear()
}

// Funciones factory para crear instancias
export const signal = (value) => new Signal(value)
export const computed = (fn) => new Computed(fn)
export const effect = (fn) => new Effect(fn)

// Exports de clases para casos avanzados
export {
    Signal,
    Computed,
    Effect,
    Batch
}